if (!customElements.get('halden-cart-upsell-carousel')) {
  class HaldenCartUpsellCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.carouselInitialized === 'true') return;
      this.dataset.carouselInitialized = 'true';

      this.track = this.querySelector('[data-upsell-track]');
      this.prevButton = this.querySelector('[data-upsell-prev]');
      this.nextButton = this.querySelector('[data-upsell-next]');
      this.dotsContainer = this.querySelector('[data-upsell-dots]');
      this.items = Array.from(this.track?.querySelectorAll('.halden-cart-upsell__item') || []);
      this.currentIndex = 0;
      this.scrollTimer = null;

      this.onPrev = () => this.goTo(this.currentIndex - 1);
      this.onNext = () => this.goTo(this.currentIndex + 1);
      this.onScroll = () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.syncFromScroll(), 60);
      };
      this.onResize = () => this.goTo(this.currentIndex, false);

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });

      this.buildDots();
      this.updateControls();

      if ('ResizeObserver' in window && this.track) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      window.clearTimeout(this.scrollTimer);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.dotButtons?.forEach((dot, index) => {
        dot.removeEventListener('click', this.dotHandlers?.[index]);
      });
    }

    buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.dotButtons = [];
      this.dotHandlers = [];

      this.items.forEach((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'halden-cart-upsell__dot';
        dot.setAttribute('aria-label', `Show recommendation ${index + 1}`);
        const handler = () => this.goTo(index);
        dot.addEventListener('click', handler);
        this.dotsContainer.appendChild(dot);
        this.dotButtons.push(dot);
        this.dotHandlers.push(handler);
      });

      const singleItem = this.items.length <= 1;
      this.dotsContainer.hidden = singleItem;
      if (this.prevButton) this.prevButton.hidden = singleItem;
      if (this.nextButton) this.nextButton.hidden = singleItem;
    }

    getSlideWidth() {
      return this.track?.clientWidth || 0;
    }

    goTo(index, smooth = true) {
      if (!this.track || !this.items.length) return;
      const nextIndex = Math.max(0, Math.min(index, this.items.length - 1));
      this.currentIndex = nextIndex;

      this.track.scrollTo({
        left: this.getSlideWidth() * nextIndex,
        behavior: smooth ? 'smooth' : 'auto',
      });

      this.updateControls();
    }

    syncFromScroll() {
      if (!this.track || !this.items.length) return;
      const width = this.getSlideWidth();
      if (!width) return;
      const index = Math.max(0, Math.min(Math.round(this.track.scrollLeft / width), this.items.length - 1));
      if (index !== this.currentIndex) {
        this.currentIndex = index;
      }
      this.updateControls();
    }

    updateControls() {
      const singleItem = this.items.length <= 1;
      if (this.prevButton) {
        this.prevButton.hidden = singleItem;
        this.prevButton.disabled = this.currentIndex <= 0;
      }
      if (this.nextButton) {
        this.nextButton.hidden = singleItem;
        this.nextButton.disabled = this.currentIndex >= this.items.length - 1;
      }

      this.dotButtons?.forEach((dot, index) => {
        const active = index === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }
  }

  customElements.define('halden-cart-upsell-carousel', HaldenCartUpsellCarousel);
}

if (!customElements.get('halden-cart-upsell-item')) {
  class HaldenCartUpsellItem extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.variantSelect = this.querySelector('[data-upsell-variant]');
      this.addButton = this.querySelector('[data-upsell-add]');
      this.price = this.querySelector('[data-upsell-price]');
      this.compare = this.querySelector('[data-upsell-compare]');
      this.image = this.querySelector('.halden-cart-upsell__image');
      this.error = this.querySelector('[data-upsell-error]');
      this.spinner = this.querySelector('[data-upsell-spinner]');
      this.label = this.querySelector('[data-upsell-add-label]');
      this.defaultButtonLabel = this.label?.textContent?.trim() || 'Add';

      this.variantSelect?.addEventListener('change', () => this.onVariantChange());
      this.addButton?.addEventListener('click', () => this.onAdd());
      this.onVariantChange();
    }

    onVariantChange() {
      const option = this.variantSelect?.selectedOptions?.[0];
      if (!option) return;

      if (this.price && option.dataset.price) this.price.textContent = option.dataset.price;

      if (this.compare) {
        const compare = option.dataset.compare || '';
        this.compare.textContent = compare;
        this.compare.classList.toggle('hidden', !compare);
      }

      if (this.image && option.dataset.image) {
        this.image.src = option.dataset.image;
        this.image.removeAttribute('srcset');
      }

      const available = option.dataset.available !== 'false';
      if (this.addButton) {
        this.addButton.disabled = !available;
        this.addButton.setAttribute('aria-disabled', available ? 'false' : 'true');
      }
      if (this.label) this.label.textContent = available ? this.defaultButtonLabel : 'Sold out';
    }

    getVariantId() {
      return this.variantSelect?.value || this.dataset.defaultVariantId;
    }

    setLoading(loading) {
      if (!this.addButton) return;
      this.addButton.disabled = loading;
      this.addButton.setAttribute('aria-disabled', loading ? 'true' : 'false');
      this.spinner?.classList.toggle('hidden', !loading);
      this.label?.classList.toggle('hidden', loading);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || 'Unable to add this item to your cart.';
      this.error.classList.remove('hidden');
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.classList.add('hidden');
    }

    async onAdd() {
      const variantId = this.getVariantId();
      if (!variantId || this.addButton?.disabled) return;

      this.clearError();
      this.setLoading(true);

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer?.getSectionsToRender?.().map((section) => section.id) || [
          'cart-drawer',
          'cart-icon-bubble',
        ];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('properties[_halden_upsell]', 'true');
        if (this.dataset.upsellCollection) {
          formData.append('properties[_halden_upsell_collection]', this.dataset.upsellCollection);
        }
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add this item to your cart.');
        }

        if (cartDrawer?.renderContents && data.sections) {
          cartDrawer.renderContents(data);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'halden-cart-upsell',
            productVariantId: variantId,
            cartData: data,
          });
        }

        if (!cartDrawer?.renderContents) {
          const drawerItems = document.querySelector('cart-drawer-items');
          if (drawerItems?.onCartUpdate) await drawerItems.onCartUpdate();
        }

        if (cartDrawer?.open && !cartDrawer.classList.contains('active')) {
          cartDrawer.open();
        }
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('halden-cart-upsell-item', HaldenCartUpsellItem);
}
